import React, { useEffect, useMemo, useState } from 'react'
import { useSWRConfig } from 'swr'
import { useSearchParams } from 'react-router-dom'
import { useGuard } from '../../hooks/auth'
import {
  ComparisonSeries,
  fmtMinutes,
  useEventComparisonTimeline,
  useEventPublic,
  useEventCommercial,
  useEventCampaignAttribution,
  useEventCrossRecurrence,
} from '../../hooks/event-comparison'
import EventPicker, { PickedEvent } from '../../components/EventComparison/EventPicker'
import ProducerPicker, { PickedProducer } from '../../components/EventComparison/ProducerPicker'
import SalesCurveComparison, {
  CurveSeries,
} from '../../components/EventComparison/SalesCurveComparison'
import PublicBuckets from '../../components/EventComparison/PublicBuckets'
import InsightsSection from '../../components/EventComparison/InsightCards'
import RitmoSection from '../../components/EventComparison/RitmoSection'
import SetoresComparison from '../../components/EventComparison/SetoresComparison'
import CommercialBehavior from '../../components/EventComparison/CommercialBehavior'
import OriginComparison from '../../components/EventComparison/OriginComparison'
import CrossRecurrence from '../../components/EventComparison/CrossRecurrence'
import IndicatorCard from '../../components/DashboardCharts/IndicatorCard'
import { ToggleLabel } from '../../components/DashboardCharts/styles'
import {
  Page,
  LoadingOverlay,
  SpinLogo,
  LoadingText,
  PageTitle,
  PageSub,
  EntryTabs,
  EntryTab,
  FiltersCard,
  FilterField,
  FieldLabel,
  ControlsRow,
  PeriodSeg,
  PeriodBtn,
  Legend,
  LegendItem,
  Dot,
  KpiRow,
  KpiCard,
  KpiLabel,
  KpiVals,
  KpiA,
  KpiVs,
  KpiB,
  Delta,
  SectionTitle,
  TableScroll,
  Table,
  DeltaCell,
  SERIES_BASE,
  SERIES_COMPARE,
} from '../../components/EventComparison/styles'

// Janela do período: em dias (D+n) ou em minutos desde a 1ª venda (OP-3517).
interface PeriodOption {
  label: string
  days: number | null
  minutes?: number
}
const PERIODS: PeriodOption[] = [
  { label: '30min', days: null, minutes: 30 },
  { label: '1h', days: null, minutes: 60 },
  { label: '6h', days: null, minutes: 360 },
  { label: 'D+1', days: 1 },
  { label: 'D+7', days: 7 },
  { label: 'D+15', days: 15 },
  { label: 'D+30', days: 30 },
  { label: 'D+60', days: 60 },
  { label: 'D+90', days: 90 },
  { label: 'Todo o período', days: null },
]
const ALL_PERIOD = PERIODS[PERIODS.length - 1]
const MARKS = [0, 1, 3, 7, 15, 30, 45, 60, 90]

const fmtInt = (n: number) => Math.round(n).toLocaleString('pt-BR')
const fmtBRL2 = (n: number) =>
  'R$ ' + n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const cumulative = (arr: number[]) => {
  let s = 0
  return arr.map((v) => (s += v))
}
const deltaPct = (a: number, b: number) => (a === 0 ? 0 : ((b - a) / a) * 100)

// Loading no padrão do Borderô: logo BD espelhando + mensagens rotativas.
const BD_MARK = `${process.env.PUBLIC_URL || ''}/favicon.svg`
const LOADING_MSGS = [
  'Alinhando os ciclos de venda…',
  'Somando os eventos…',
  'Comparando os públicos…',
  'Cruzando os canais…',
  'Montando o comparativo…',
]
const ComparisonLoading: React.FC = () => {
  const [i, setI] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % LOADING_MSGS.length), 1400)
    return () => clearInterval(t)
  }, [])
  return (
    <LoadingOverlay>
      <SpinLogo src={BD_MARK} alt="Carregando" />
      <LoadingText>{LOADING_MSGS[i]}</LoadingText>
    </LoadingOverlay>
  )
}

interface Props {
  permissions?: string[]
}

export function EventComparison({ permissions = ['DP19'] }: Props) {
  useGuard(permissions)

  const [searchParams] = useSearchParams()
  const [entryMode, setEntryMode] = useState<'ctx' | 'std'>('std')
  const [baseEvents, setBaseEvents] = useState<PickedEvent[]>([])
  const [compareEvents, setCompareEvents] = useState<PickedEvent[]>([])
  const [producerBase, setProducerBase] = useState<PickedProducer | null>(null)
  const [producerCompare, setProducerCompare] = useState<PickedProducer | null>(null)

  const baseIds = useMemo(() => baseEvents.map((e) => e.id), [baseEvents])
  const compareIds = useMemo(() => compareEvents.map((e) => e.id), [compareEvents])
  const [period, setPeriod] = useState<PeriodOption>(ALL_PERIOD) // default: Todo o Período (janela completa)
  const [mode, setMode] = useState<'daily' | 'cum'>('daily')
  const [excludeCourtesy, setExcludeCourtesy] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  // Atualiza (revalida) todos os dados da tela sem perder a seleção de eventos.
  const { mutate } = useSWRConfig()
  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await mutate(() => true, undefined, { revalidate: true })
    } finally {
      setRefreshing(false)
    }
  }

  // Entrada contextual/deep-link: ?eventIds=<base>[,<comparado>] pré-preenche os pickers.
  useEffect(() => {
    const parts = (searchParams.get('eventIds') || '')
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isInteger(n) && n > 0)
    if (parts[0]) {
      setBaseEvents([{ id: parts[0], nome: `Evento #${parts[0]}` }])
      setEntryMode(parts[1] ? 'std' : 'ctx')
    }
    if (parts[1]) setCompareEvents([{ id: parts[1], nome: `Evento #${parts[1]}` }])
  }, [searchParams])

  // cada lado é um GRUPO de eventos somados (OP-3417); a timeline volta 1 série por grupo (na ordem).
  const { comparison, comparisonLoading } = useEventComparisonTimeline(
    [baseIds, compareIds],
    period.days,
    period.minutes
  )

  // Janela por minutos (OP-3517): série em baldes de `bucket` minutos; os blocos
  // de público/comercial/origem (endpoints por DATA) aproximam para o dia do D+0.
  const minuteMode = period.minutes != null
  const bucket = comparison?.bucketMinutes || 5
  const sectionMaxDay = minuteMode ? 0 : period.days
  // rótulo do INÍCIO do balde i (eixo do gráfico) e do FIM (acumulado na tabela)
  const bucketStart = (i: number) => (i === 0 ? '0' : `+${fmtMinutes(i * bucket)}`)
  const bucketEnd = (i: number) => `até ${fmtMinutes((i + 1) * bucket)}`

  const baseSeries = comparison?.eventos?.[0]
  const compareSeries = comparison?.eventos?.[1]

  // valor por dia respeitando o toggle "excluir cortesias" (usa pagos)
  const dayValue = (e: ComparisonSeries) =>
    e.dias.map((d) => (excludeCourtesy ? d.ingressosPagos : d.ingressos))

  const curveSeries: CurveSeries[] = useMemo(() => {
    const out: CurveSeries[] = []
    if (baseSeries) {
      const daily = dayValue(baseSeries)
      out.push({
        nome: baseSeries.nome || 'Evento base',
        cor: SERIES_BASE,
        pontos: mode === 'cum' ? cumulative(daily) : daily,
      })
    }
    if (compareSeries) {
      const daily = dayValue(compareSeries)
      out.push({
        nome: compareSeries.nome || 'Evento comparado',
        cor: SERIES_COMPARE,
        pontos: mode === 'cum' ? cumulative(daily) : daily,
      })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseSeries, compareSeries, mode, excludeCourtesy])

  // rótulos do eixo X nas janelas por minuto (0, +5min, +10min…)
  const curveLabels = useMemo(() => {
    if (!minuteMode) return undefined
    const len = curveSeries.reduce((m, s) => Math.max(m, s.pontos.length), 0)
    return Array.from({ length: len }, (_, i) => bucketStart(i))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curveSeries, minuteMode, bucket])

  // KPIs a partir dos totais (respeitando o toggle)
  const kpis = useMemo(() => {
    if (!baseSeries || !compareSeries) return null
    const val = (e: ComparisonSeries) => ({
      ingressos: excludeCourtesy ? e.totais.ingressosPagos : e.totais.ingressos,
      receita: e.totais.receita,
      pedidos: e.totais.pedidos,
      ticket: e.totais.receita / Math.max(e.totais.ingressosPagos, 1),
    })
    const a = val(baseSeries)
    const b = val(compareSeries)
    return [
      { lbl: 'Ingressos vendidos', a: fmtInt(a.ingressos), b: fmtInt(b.ingressos), d: deltaPct(a.ingressos, b.ingressos) },
      { lbl: 'Receita bruta', a: fmtBRL2(a.receita), b: fmtBRL2(b.receita), d: deltaPct(a.receita, b.receita) },
      { lbl: 'Ticket médio', a: fmtBRL2(a.ticket), b: fmtBRL2(b.ticket), d: deltaPct(a.ticket, b.ticket) },
      { lbl: 'Pedidos', a: fmtInt(a.pedidos), b: fmtInt(b.pedidos), d: deltaPct(a.pedidos, b.pedidos) },
    ]
  }, [baseSeries, compareSeries, excludeCourtesy])

  // tabela por marco (acumulado) — janelas por minuto listam todos os baldes
  const tableRows = useMemo(() => {
    if (!baseSeries || !compareSeries) return []
    const ca = cumulative(dayValue(baseSeries))
    const cb = cumulative(dayValue(compareSeries))
    const len = Math.max(ca.length, cb.length)
    const marks = minuteMode ? Array.from({ length: len }, (_, i) => i) : MARKS
    return marks.filter((d) => d < len).map((d) => {
      const va = d < ca.length ? ca[d] : null
      const vb = d < cb.length ? cb[d] : null
      const dp = va != null && vb != null && va > 0 ? ((vb - va) / va) * 100 : null
      return { d, va, vb, diff: va != null && vb != null ? vb - va : null, dp }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseSeries, compareSeries, excludeCourtesy, minuteMode])

  // Perfil do público de cada GRUPO na janela [D0, D0+período]. D0 vem do timeline.
  // Nas janelas por minuto, aproxima para o dia completo do D+0 (endpoints por data).
  const basePublic = useEventPublic(baseIds, baseSeries?.d0, sectionMaxDay)
  const comparePublic = useEventPublic(compareIds, compareSeries?.d0, sectionMaxDay)

  // Quebras comerciais/comportamentais na mesma janela.
  const baseCommercial = useEventCommercial(baseIds, baseSeries?.d0, sectionMaxDay)
  const compareCommercial = useEventCommercial(compareIds, compareSeries?.d0, sectionMaxDay)

  // Origem das vendas (OP-3419) por grupo, na mesma janela [D0, D0+período]
  const baseOrigin = useEventCampaignAttribution(baseIds, baseSeries?.d0, sectionMaxDay)
  const compareOrigin = useEventCampaignAttribution(compareIds, compareSeries?.d0, sectionMaxDay)

  // Recorrência cruzada: clientes (CPF) que compraram os dois lados (sem janela)
  const crossRecurrence = useEventCrossRecurrence(baseIds, compareIds)

  // loading agregado de todos os blocos — usado no overlay ao Filtrar / trocar seleção
  const anyLoading =
    refreshing ||
    comparisonLoading ||
    basePublic.loading ||
    comparePublic.loading ||
    baseCommercial.loading ||
    compareCommercial.loading ||
    baseOrigin.loading ||
    compareOrigin.loading ||
    crossRecurrence.loading

  // Insights (Fase 2): leituras por regra sobre os números já buscados.
  const insightsInput = useMemo(() => {
    if (!baseSeries || !compareSeries) return null
    const cumBase = cumulative(dayValue(baseSeries))
    const cumCompare = cumulative(dayValue(compareSeries))
    const recPct = (p: typeof basePublic) =>
      p.recurrence
        ? (excludeCourtesy ? p.recurrence.semCortesia : p.recurrence.comCortesia).recorrentesPct
        : 0
    return {
      baseName: baseSeries.nome || 'Evento base',
      compareName: compareSeries.nome || 'Evento comparado',
      baseCum: cumBase,
      compareCum: cumCompare,
      baseTotal: cumBase[cumBase.length - 1] || 0,
      compareTotal: cumCompare[cumCompare.length - 1] || 0,
      baseTicket: baseSeries.totais.receita / Math.max(baseSeries.totais.ingressosPagos, 1),
      compareTicket: compareSeries.totais.receita / Math.max(compareSeries.totais.ingressosPagos, 1),
      baseRecPct: recPct(basePublic),
      compareRecPct: recPct(comparePublic),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseSeries, compareSeries, excludeCourtesy, basePublic, comparePublic])

  const ready = baseIds.length > 0 && compareIds.length > 0

  return (
    <Page>
      {ready && anyLoading && <ComparisonLoading />}
      <EntryTabs>
        <EntryTab $active={entryMode === 'ctx'} onClick={() => setEntryMode('ctx')}>
          ◎ A partir deste evento
        </EntryTab>
        <EntryTab $active={entryMode === 'std'} onClick={() => setEntryMode('std')}>
          ⇄ Escolher eventos
        </EntryTab>
      </EntryTabs>

      <PageTitle>Comparativo de Eventos</PageTitle>
      <PageSub>
        Comparação pelo ciclo de vendas — alinhado por <b>D+0</b> (1º dia de venda de cada evento),
        não pela data-calendário.
      </PageSub>

      <FiltersCard>
        <FilterField>
          <FieldLabel>Produtor base</FieldLabel>
          <ProducerPicker
            value={producerBase}
            color={SERIES_BASE}
            onChange={(p) => {
              setProducerBase(p)
              setBaseEvents([])
            }}
          />
        </FilterField>
        <FilterField>
          <FieldLabel>Produtor comparado</FieldLabel>
          <ProducerPicker
            value={producerCompare}
            color={SERIES_COMPARE}
            onChange={(p) => {
              setProducerCompare(p)
              setCompareEvents([])
            }}
          />
        </FilterField>

        <FilterField>
          <FieldLabel>Evento base <span style={{ textTransform: 'none', color: '#B0B0B0', fontWeight: 500 }}>(some vários = 1 evento)</span></FieldLabel>
          <EventPicker
            label="Evento base"
            color={SERIES_BASE}
            values={baseEvents}
            onChange={setBaseEvents}
            clienteId={producerBase?.id}
          />
        </FilterField>
        <FilterField>
          <FieldLabel>Evento comparado <span style={{ textTransform: 'none', color: '#B0B0B0', fontWeight: 500 }}>(some vários = 1 evento)</span></FieldLabel>
          <EventPicker
            label="Evento comparado"
            color={SERIES_COMPARE}
            values={compareEvents}
            onChange={setCompareEvents}
            clienteId={producerCompare?.id}
          />
        </FilterField>

        <ControlsRow>
          <FilterField style={{ gap: 6 }}>
            <FieldLabel>Período (janela desde a 1ª venda)</FieldLabel>
            <PeriodSeg>
              {PERIODS.map((p) => (
                <PeriodBtn key={p.label} $active={period.label === p.label} onClick={() => setPeriod(p)}>
                  {p.label}
                </PeriodBtn>
              ))}
            </PeriodSeg>
            {minuteMode && (
              <span style={{ fontSize: 11, color: '#B0B0B0' }}>
                Curva, KPIs e tabela usam a janela exata de {fmtMinutes(period.minutes as number)} desde a
                1ª venda; público, setores, comportamento e origem consideram o dia completo do D+0.
              </span>
            )}
          </FilterField>
          <ToggleLabel style={{ alignSelf: 'flex-end' }}>
            <input type="checkbox" checked={excludeCourtesy} onChange={(e) => setExcludeCourtesy(e.target.checked)} />
            Excluir cortesias
          </ToggleLabel>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              alignSelf: 'flex-end',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: SERIES_BASE,
              color: '#fff',
              border: 0,
              borderRadius: 9,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: refreshing ? 'default' : 'pointer',
              opacity: refreshing ? 0.7 : 1,
            }}
          >
            <span style={{ display: 'inline-block', transform: refreshing ? 'rotate(180deg)' : 'none', transition: 'transform .3s' }}>↻</span>
            {refreshing ? 'Atualizando…' : 'Filtrar / Atualizar'}
          </button>
          <Legend>
            <LegendItem><Dot $color={SERIES_BASE} /> Base</LegendItem>
            <LegendItem><Dot $color={SERIES_COMPARE} /> Comparado</LegendItem>
          </Legend>
        </ControlsRow>
      </FiltersCard>

      {!ready ? (
        <IndicatorCard title="Selecione os eventos">
          <p style={{ color: '#B0B0B0', fontSize: 14, margin: '4px 0 0' }}>
            Escolha o <b>evento base</b> e o <b>evento comparado</b> (ambos vinculados ao seu login)
            para ver a comparação por ciclo de vendas.
          </p>
        </IndicatorCard>
      ) : (
        <>
          {kpis && (
            <KpiRow>
              {kpis.map((k) => (
                <KpiCard key={k.lbl}>
                  <KpiLabel>{k.lbl}</KpiLabel>
                  <KpiVals>
                    <KpiA>{k.a}</KpiA>
                    <KpiVs>vs</KpiVs>
                    <KpiB>{k.b}</KpiB>
                  </KpiVals>
                  <Delta $up={k.d >= 0}>
                    {k.d >= 0 ? '▲' : '▼'} {Math.abs(k.d).toFixed(1)}% comparado
                  </Delta>
                </KpiCard>
              ))}
            </KpiRow>
          )}

          <div style={{ marginBottom: 24 }}>
            <SalesCurveComparison
              series={curveSeries}
              mode={mode}
              onModeChange={setMode}
              labels={curveLabels}
              xTitle={minuteMode ? 'Tempo desde a 1ª venda (janela exata)' : undefined}
              title={minuteMode ? 'Evolução das vendas desde a 1ª venda' : undefined}
            />
          </div>

          <SectionTitle>Tabela comparativa por marco</SectionTitle>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>{minuteMode ? 'Janela' : 'Dia de venda'}</th>
                  <th>Base</th>
                  <th>Comparado</th>
                  <th>Δ ingressos</th>
                  <th>Δ %</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => (
                  <tr key={r.d}>
                    <td style={{ fontWeight: 640, fontSize: 12 }}>
                      {minuteMode ? bucketEnd(r.d) : `D+${r.d}`}
                    </td>
                    <td style={{ color: SERIES_BASE }}>{r.va != null ? fmtInt(r.va) : '—'}</td>
                    <td style={{ color: SERIES_COMPARE }}>{r.vb != null ? fmtInt(r.vb) : '—'}</td>
                    <td>{r.diff != null ? (r.diff >= 0 ? '+' : '') + fmtInt(r.diff) : '—'}</td>
                    <td>
                      {r.dp != null ? (
                        <DeltaCell $up={r.dp >= 0}>
                          {r.dp >= 0 ? '▲' : '▼'} {Math.abs(r.dp).toFixed(1)}%
                        </DeltaCell>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
                {comparisonLoading && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#B0B0B0' }}>
                      Carregando…
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableScroll>

          <RitmoSection
            baseSeries={baseSeries}
            compareSeries={compareSeries}
            excludeCourtesy={excludeCourtesy}
            bucketMinutes={minuteMode ? bucket : undefined}
          />

          <SetoresComparison
            baseName={baseSeries?.nome || 'Base'}
            compareName={compareSeries?.nome || 'Comparado'}
            baseEventIds={baseIds}
            compareEventIds={compareIds}
            baseSectors={basePublic.sectors}
            compareSectors={comparePublic.sectors}
          />

          <CommercialBehavior
            baseName={baseSeries?.nome || 'Base'}
            compareName={compareSeries?.nome || 'Comparado'}
            baseCommercial={baseCommercial}
            compareCommercial={compareCommercial}
          />

          <OriginComparison
            baseName={baseSeries?.nome || 'Base'}
            compareName={compareSeries?.nome || 'Comparado'}
            baseOrigin={baseOrigin.data}
            compareOrigin={compareOrigin.data}
            baseLoading={baseOrigin.loading}
            compareLoading={compareOrigin.loading}
          />

          <div style={{ marginTop: 24 }}>
            <PublicBuckets
              baseName={baseSeries?.nome || 'Base'}
              compareName={compareSeries?.nome || 'Comparado'}
              basePublic={basePublic}
              comparePublic={comparePublic}
              excludeCourtesy={excludeCourtesy}
              courtesyBase={
                baseSeries && baseSeries.totais.ingressos > 0
                  ? (baseSeries.totais.cortesias / baseSeries.totais.ingressos) * 100
                  : 0
              }
              courtesyCompare={
                compareSeries && compareSeries.totais.ingressos > 0
                  ? (compareSeries.totais.cortesias / compareSeries.totais.ingressos) * 100
                  : 0
              }
            />
          </div>

          <CrossRecurrence
            baseName={baseSeries?.nome || 'Base'}
            compareName={compareSeries?.nome || 'Comparado'}
            data={crossRecurrence}
            excludeCourtesy={excludeCourtesy}
          />

          {insightsInput && <InsightsSection input={insightsInput} />}
        </>
      )}
    </Page>
  )
}

export default EventComparison
